import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleLineChartProps,
  getMultipleLineStrCategoriesChartProps,
  magnitudePointFormatter,
  tooltipFormatterMinMaxMonthly,
} from '../utils/compass'
import { chartPalette } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

export namespace Brf {
  export namespace VolumeExportado {
    const CONTEXT_ID = 'equity:brf:volume_exportado'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'mensal' | 'mensal_yoy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'mensal':
            return getMensalChartProps(config, data)
          case 'mensal_yoy':
            return getMensalYoyChartProps(config, data)
        }
      },
    }

    const getMensalChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineStrCategoriesChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          tooltip: {
            shared: true,
            split: undefined,
            formatter: tooltipFormatterMinMaxMonthly({
              pointKeysAreTimestamps: true,
              valueSuffix: '%',
            }),
          },
        },
        {
          color: chartPalette.slice(0, data.data.length).reverse(),
          type: Array(data.data.length).fill('spline'),
          lineWidth: data.data.map((_, idx) =>
            idx === data.data.length - 1
              ? 4
              : idx > data.data.length - 3
              ? 3
              : 1
          ),
          zIndex: data.data.map((_, idx) =>
            idx === data.data.length - 1
              ? 100
              : idx > data.data.length - 3
              ? 50
              : 10
          ),
        }
      )(config, data)

    const getMensalYoyChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [true, true],
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      },
      {
        type: Array(2).fill('spline'),
      }
    )
  }

  export namespace Frango {
    const CONTEXT_ID = 'equity:brf:frango'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'atacado_sp'
        | 'atacado_sp_yoy'
        | 'exportacao_usd'
        | 'exportacao_usd_yoy'
        | 'exportacao_brl'
        | 'exportacao_brl_yoy'
        | 'exp_dom'
        | 'exp_dom_ratio'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'atacado_sp':
            return getSingleLineChartProps(config, data)
          case 'atacado_sp_yoy':
          case 'exportacao_usd_yoy':
          case 'exportacao_brl_yoy':
            return getSingleLineYoyChartProps(config, data)
          case 'exportacao_usd':
          case 'exportacao_brl':
          case 'exp_dom':
          case 'exp_dom_ratio':
            return getMultiLineChartProps(config, data)
        }
      },
    }

    const getSingleLineChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
      tooltip: { xDateFormat: '%B %Y' },
      xAxis: { labels: { format: '{value:%Y}' } },
    })

    const getSingleLineYoyChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
      convertToPercentages: [true],
      tooltip: { xDateFormat: '%B %Y' },
      xAxis: { labels: { format: '{value:%Y}' } },
    })

    const getMultiLineChartProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
      tooltip: { xDateFormat: '%B %Y' },
      xAxis: { labels: { format: '{value:%Y}' } },
    })
  }

  export namespace Graos {
    const CONTEXT_ID = 'equity:brf:graos'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'embrapa_mix'
        | 'embrapa_mix_yoy'
        | 'milho_norte_parana'
        | 'milho_norte_parana_yoy'
        | 'soja_norte_parana'
        | 'soja_norte_parana_yoy'
        | 'soja_br_internacional'
        | 'milho_br_internacional'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'embrapa_mix':
            return getTwoAxisChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const isPercentChart = (chart: string) =>
      chart === 'embrapa_mix_yoy' ||
      chart === 'milho_norte_parana_yoy' ||
      chart === 'soja_norte_parana_yoy'

    const getTwoAxisChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
        yAxis: [
          {
            id: 'cost',
            opposite: true,
            title: { text: 'Custo' },
          },
          {
            id: 'default',
          },
        ],
      },
      {
        yAxis: ['default', 'cost'],
      }
    )

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        ...(isPercentChart(config.chart)
          ? { convertToPercentages: Array(data.data.length).fill(true) }
          : {}),
        tooltip: {
          ...(config.chart === 'soja_norte_parana'
            ? { pointFormatter: magnitudePointFormatter() }
            : {}),
          xDateFormat: '%B %Y',
        },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)
  }

  export namespace Margens {
    const CONTEXT_ID = 'equity:brf:margens'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'spread_embrapa_pr' | 'spread_graos_pr' | 'mercado_brf'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        ...(config.chart === 'mercado_brf'
          ? { convertToPercentages: Array(data.data.length).fill(true) }
          : {}),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:brf:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'ev_ebitda_brfs3' | 'ev_ebitda_mrfg3' | 'ev_ebitda_jbss3_tsn_hrl'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ev_ebitda_brfs3':
          case 'ev_ebitda_mrfg3':
            return getMultipleLineChartPropsWrapper(config, data as ChartData)
          case 'ev_ebitda_jbss3_tsn_hrl':
            return getPeersChartProps(config, data as ChartData)
        }
      },
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getPeersChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
        },
        { name: ['JBSS3 BZ EQUITY', 'TSN US EQUITY', 'HRL US EQUITY'] }
      )(config, data)
  }
}
